import { MainButton } from 'elements/button/MainButton';
import { EColors } from 'libs/styles/styling';
import { useIsWide } from 'libs/utils/logic/ui/responsiveLogic';
import { useAnalytics } from 'libs/utils/site/analytics';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    logo?: string;
    logoMarginTop?: string;
    logoText?: string;
    logoSubtext?: string;
    link?: string;
    linkText?: string;
    className?: string;

    children: JSX.Element;
}

export const CustomHeader = (props: Props): JSX.Element => {
    const isWide = useIsWide();
    const analytics = useAnalytics();

    return (
        <S_CustomHeader
            className={props.className}
            onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
            }}
        >
            <S_Row>
                <S_LogoContainer>
                    {props.logo && (
                        <S_Logo
                            src={props.logo}
                            style={{
                                marginTop: props.logoMarginTop || '0px'
                            }}
                        />
                    )}
                    {(props.logoText || props.logoSubtext) && (
                        <S_LogoTextContainer>
                            {props.logoText && <S_LogoText>{props.logoText}</S_LogoText>}
                            {props.logoText && props.logoSubtext && <S_Split />}
                            {props.logoSubtext && (
                                <S_LogoSubtext>{props.logoSubtext}</S_LogoSubtext>
                            )}
                        </S_LogoTextContainer>
                    )}
                </S_LogoContainer>
                {isWide && <S_Buttons>{props.children}</S_Buttons>}
                <S_LinkContainer>
                    {props.link && (
                        <MainButton
                            type="grey"
                            iconSet="fal"
                            iconName="arrow-up-right-from-square"
                            onClick={() => {
                                analytics.track('event_ui.external_link', {
                                    source: 'entity_header',
                                    destination: props.link
                                });
                                const link = props.link?.startsWith('http')
                                    ? props.link
                                    : `https://${props.link}`;
                                window.open(link, '_blank', 'noreferrer');
                            }}
                        >
                            {props.linkText ?? Localization.WEBSITE}
                        </MainButton>
                    )}
                </S_LinkContainer>
            </S_Row>
        </S_CustomHeader>
    );
};

const S_CustomHeader = styled.div`
    background-color: white;
    border-bottom: 1px solid ${EColors.GREY5};
    width: 100%;
    z-index: 1;
    padding: 0 10px 0 10px;
    flex-shrink: 0;
    @media only screen and (min-width: 1259px) {
        padding: 0 25px 0 15px;
    }
`;

const S_LogoContainer = styled.div`
    display: flex;
    align-items: center;
    flex-grow: 1;
`;

const S_Logo = styled.img`
    height: unset;
    width: 60px;
    margin: 0 15px 0 0;
    @media only screen and (min-width: 1259px) {
        height: 50px;
        width: unset;
    }
`;

const S_LogoTextContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const S_LogoText = styled.div`
    color: ${EColors.ALMOST_BLACK};
    font-size: 15px;
    @media only screen and (min-width: 1259px) {
        font-size: 16px;
    }
`;

const S_Split = styled.div`
    height: 4px;
`;

const S_LogoSubtext = styled.div`
    color: ${EColors.GREY4};
    font-size: 14px;
`;

const S_Row = styled.div`
    min-height: 75px;
    display: flex;
    align-items: strech;
    justify-content: space-between;
`;

const S_Buttons = styled.div`
    display: flex;
    flex-grow: 1;
    justify-content: center;
`;

const S_LinkContainer = styled.div`
    display: flex;
    flex-grow: 1;
    justify-content: right;
    align-items: center;
`;
